import React from 'react'
import * as yup from 'yup'
import { Formik } from 'formik'
import { AllowedUserFieldsEnum } from 'shared/accountNameConfig'
import { getValidationSchemaForUserFields } from 'shared/entities-validator/user-fields-validators'
import { t } from '@lingui/macro'
import { useVerificationFormFieldsContext } from 'front-end/src/pages/auth/verification-user-form-step/VerificationFormFieldsContext'
import { useLoginContext } from 'front-end/src/pages/auth/login/LoginContext'
import { observer } from 'mobx-react'

type WithVerificationFormikProps = {
  onSubmit: (formData: Record<AllowedUserFieldsEnum, string>) => void
}

const useFormikConfig = (): {
  validationSchemaWithoutPassword: yup.AnySchema
  initialValues: Record<AllowedUserFieldsEnum, string>
} => {
  const { fields: requiredFields } = useVerificationFormFieldsContext()
  const { userData, isAccountNameLoginOn, userIdentity } = useLoginContext()

  const errorMessages = {
    defaultNonNullMessage: t`must not be empty`,
    emailRequiredMessage: t`email is required`
  }

  return React.useMemo(() => {
    return {
      validationSchemaWithoutPassword: yup.object().shape(
        getValidationSchemaForUserFields<typeof errorMessages>({
          errorMessages,
          // some fields in user profile can be empty, so we don't need
          // to require them at FE.
          // BE will compare values with values in user profile
          requireAdditionalFields: true,
          requiredFields
        })
      ),
      initialValues: {
        ...(Object.fromEntries(
          requiredFields.map((field) => [field, userData?.[field] ?? ''])
        ) as Record<AllowedUserFieldsEnum, string>),
        [AllowedUserFieldsEnum.ACCOUNT_NAME]: userIdentity
      }
    }
  }, [requiredFields, isAccountNameLoginOn])
}

export const WithVerificationFormik: React.FC<WithVerificationFormikProps> =
  observer(({ children, onSubmit }) => {
    const { validationSchemaWithoutPassword, initialValues } = useFormikConfig()

    return (
      <Formik
        onSubmit={onSubmit}
        initialValues={initialValues}
        validateOnMount={true}
        enableReinitialize
        validationSchema={validationSchemaWithoutPassword}
      >
        {children}
      </Formik>
    )
  })

WithVerificationFormik.displayName = 'WithVerificationFormik'
